import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { workOrdersAPI, propertiesAPI, apartmentsAPI, filesAPI, fileViewUrl } from '../services/api';
import Modal from '../components/common/Modal';
import { toast } from '../components/common/Toast';
import { Plus, Search, AlertCircle, Clock, CheckCircle2, RefreshCw, Paperclip, Image, X, Edit2, Trash2, Printer } from 'lucide-react';
import { fmt, statusLabel, statusClass, priorityClass, errMsg } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';

const CATS  = ['HVAC','Plumbing','Electrical','Appliances','Windows/Doors','Common Area','Flooring','Painting','Landscaping','Other'];
const CAT_KEYS = { 'HVAC':'hvac','Plumbing':'plumbing','Electrical':'electrical','Appliances':'appliances','Windows/Doors':'windowsDoors','Common Area':'commonArea','Flooring':'flooring','Painting':'painting','Landscaping':'landscaping','Other':'other' };
const EMPTY = { propertyId:'', apartmentId:'', title:'', description:'', category:'Plumbing', priority:'medium', assignedTo:'', scheduledDate:'', cost:'', notes:'' };

function isImage(url) {
  return /\.(png|jpg|jpeg|webp|heic)$/i.test(url);
}

export default function WorkOrders() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { can }  = usePermissions();
  const isStaff   = user?.role !== 'tenant';
  const canCreate = can('workOrders', 'create');
  const canEdit   = can('workOrders', 'edit');
  const canDelete = can('workOrders', 'delete');

  const [wos,     setWos]     = useState([]);
  const [props,   setProps]   = useState([]);
  const [apts,    setApts]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [statusF, setStatusF] = useState('');
  const [prioF,   setPrioF]   = useState('');
  const [modal,   setModal]   = useState(false);
  const [detail,     setDetail]     = useState(null);
  const [editWo,     setEditWo]     = useState(null);
  const [editForm,   setEditForm]   = useState(EMPTY);
  const [confirmDel, setConfirmDel] = useState(null);
  const [imageUrls,  setImageUrls]  = useState({});
  const [form,      setForm]      = useState(EMPTY);
  const [files,     setFiles]     = useState([]);
  const [saving,    setSaving]    = useState(false);
  const fileRef = useRef();

  // Pre-fetch signed URLs for all attachments when a work order detail is opened.
  // Storing URLs in state means no async work happens on click — safe on mobile Safari.
  useEffect(() => {
    if (!detail?.images?.length) { setImageUrls({}); return; }
    setImageUrls({});
    Promise.all(
      detail.images.map(async (key) => {
        if (!key || key.startsWith('/')) return [key, key];
        try {
          const res = await filesAPI.getSignedUrl(key);
          return [key, res.data.url];
        } catch {
          return [key, null];
        }
      })
    ).then(entries => setImageUrls(Object.fromEntries(entries)));
  }, [detail?._id]);

  const load = useCallback(() => {
    setLoading(true);
    const q = {};
    if (statusF) q.status   = statusF;
    if (prioF)   q.priority = prioF;
    if (search)  q.search   = search;
    const empty = Promise.resolve({ data: { data: [] } });
    const calls = isStaff
      ? [workOrdersAPI.getAll(q), propertiesAPI.getAll(), apartmentsAPI.getAll()]
      : [workOrdersAPI.getAll(q), empty, empty];
    Promise.all(calls)
      .then(([w, p, a]) => { setWos(w.data.data); setProps(p.data.data); setApts(a.data.data); })
      .catch(() => toast.error(t('failedToLoad')))
      .finally(() => setLoading(false));
  }, [statusF, prioF, search, isStaff, t]);

  useEffect(() => { const timer = setTimeout(load, 300); return () => clearTimeout(timer); }, [load]);

  const propApts     = apts.filter(a => (a.propertyId?._id || a.propertyId)?.toString() === form.propertyId);
  const editPropApts = apts.filter(a => (a.propertyId?._id || a.propertyId)?.toString() === editForm.propertyId);
  const counts   = s => wos.filter(w => w.status === s).length;

  const openModal = () => { setForm(EMPTY); setFiles([]); setModal(true); };

  const handleSave = async () => {
    if (!form.title) return toast.warning(t('title') + ' ' + t('required'));
    if (isStaff && !form.propertyId) return toast.warning(t('property') + ' ' + t('required'));
    setSaving(true);
    try {
      const fd = new FormData();
      // Append form fields, skipping empty strings to avoid ObjectId cast errors
      Object.entries(form).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) fd.append(k, v);
      });
      // Attach image/file attachments
      files.forEach(f => fd.append('images', f));
      await workOrdersAPI.create(fd);
      toast.success(t('workOrderCreated'));
      setModal(false); setForm(EMPTY); setFiles([]); load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await workOrdersAPI.update(id, { status });
      toast.success(`${t('markedAs')} ${statusLabel(status)}`);
      load(); setDetail(null);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const removeFile = idx => setFiles(fs => fs.filter((_, i) => i !== idx));

  const openEdit = (w) => {
    setEditForm({
      propertyId:    w.propertyId?._id || w.propertyId || '',
      apartmentId:   w.apartmentId?._id || w.apartmentId || '',
      title:         w.title || '',
      description:   w.description || '',
      category:      w.category || 'Plumbing',
      priority:      w.priority || 'medium',
      status:        w.status || 'open',
      assignedTo:    w.assignedTo || '',
      scheduledDate: w.scheduledDate ? w.scheduledDate.slice(0,10) : '',
      cost:          w.cost != null ? String(w.cost) : '',
      notes:         w.notes || '',
    });
    setEditWo(w);
  };

  const handleEditSave = async () => {
    if (!editForm.title) return toast.warning(t('title') + ' ' + t('required'));
    setSaving(true);
    try {
      const data = { ...editForm };
      if (!data.scheduledDate) delete data.scheduledDate;
      if (data.cost === '')        delete data.cost;
      if (data.apartmentId === '') data.apartmentId = null;
      await workOrdersAPI.update(editWo._id, data);
      toast.success(t('workOrderUpdated'));
      setEditWo(null); load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await workOrdersAPI.delete(confirmDel._id);
      toast.success('Work order deleted');
      setConfirmDel(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const PICONS = {
    high:   <AlertCircle  size={13} color="var(--rose)"/>,
    medium: <Clock        size={13} color="var(--amber)"/>,
    low:    <CheckCircle2 size={13} color="var(--emerald)"/>,
  };

  // wo = work order object, urls = optional pre-fetched signed URL map (from imageUrls state)
  const handlePrint = (wo, urls = {}) => {
    if (!wo) return;
    const esc = s => String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const nl  = s => esc(s).replace(/\n/g, '<br>');
    const getUrl = key => urls[key] || fileViewUrl(key);

    const statusColors = {
      open:'#dc2626', in_progress:'#2563eb', scheduled:'#d97706',
      completed:'#16a34a', cancelled:'#64748b',
    };
    const priorityColors = { high:'#dc2626', medium:'#d97706', low:'#16a34a' };
    const detail = wo;
    const sc = statusColors[detail.status]  || '#64748b';
    const pc = priorityColors[detail.priority] || '#64748b';

    const imageBlocks = (detail.images || []).map((key, i) =>
      isImage(key)
        ? `<div class="img-wrap"><img src="${getUrl(key)}" alt="Attachment ${i+1}" onerror="this.style.display='none'"></div>`
        : `<a class="file-link" href="${getUrl(key)}" target="_blank">📎 Attachment ${i+1}</a>`
    ).join('');

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>Work Order — ${esc(detail.title)}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:13px;color:#1e293b;background:#fff}
  .page{max-width:780px;margin:0 auto;padding:24mm 20mm}
  .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #2563eb;padding-bottom:14px;margin-bottom:18px}
  .brand{font-size:15px;font-weight:800;color:#2563eb;letter-spacing:-.01em}
  .brand span{font-weight:400;color:#94a3b8;font-size:12px;display:block;margin-top:2px}
  .wo-id{font-size:11px;color:#94a3b8;text-align:right}
  h1{font-size:19px;font-weight:800;line-height:1.3;margin-bottom:8px}
  .meta{font-size:12px;color:#64748b;margin-bottom:14px}
  .badges{display:flex;gap:7px;flex-wrap:wrap;margin-bottom:16px}
  .badge{padding:3px 10px;border-radius:20px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.04em}
  .section-label{font-size:10px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.06em;margin:16px 0 8px}
  .desc{font-size:14px;color:#475569;line-height:1.75;margin-bottom:4px}
  .grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:4px}
  .field{background:#f8fafc;border-radius:6px;padding:9px 12px;border:1px solid #e2e8f0}
  .field-label{font-size:10px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.05em;margin-bottom:3px}
  .field-value{font-size:13px;font-weight:600;color:#1e293b}
  .notes-box{background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:12px 14px;font-size:13px;color:#475569;line-height:1.7}
  .images{display:flex;flex-wrap:wrap;gap:10px;margin-top:4px}
  .img-wrap{width:160px;height:120px;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;flex-shrink:0}
  .img-wrap img{width:100%;height:100%;object-fit:cover;display:block}
  .file-link{display:inline-flex;align-items:center;padding:7px 12px;border:1px solid #e2e8f0;border-radius:8px;font-size:12px;color:#2563eb;text-decoration:none;background:#f8fafc}
  .footer{display:flex;justify-content:space-between;padding-top:14px;border-top:1px solid #e2e8f0;font-size:11px;color:#94a3b8;margin-top:28px}
  @media print{
    body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
    @page{margin:15mm}
    .page{padding:0}
    a{text-decoration:none}
  }
</style>
</head>
<body>
<div class="page">
  <div class="header">
    <div class="brand">PropManager<span>Work Order Report</span></div>
    <div class="wo-id">
      <div style="font-weight:700;color:#1e293b;font-size:13px">#${esc(detail._id?.slice(-8).toUpperCase())}</div>
      <div>Created: ${esc(fmt.date(detail.createdAt))}</div>
    </div>
  </div>

  <h1>${esc(detail.title)}</h1>
  <div class="meta">
    ${detail.propertyId?.name ? `${esc(detail.propertyId.name)}` : ''}
    ${detail.apartmentId ? ` · Unit #${esc(detail.apartmentId.unit)}` : ''}
  </div>
  <div class="badges">
    <span class="badge" style="background:${sc}18;color:${sc}">${esc(statusLabel(detail.status))}</span>
    <span class="badge" style="background:${pc}18;color:${pc}">${esc(statusLabel(detail.priority))} Priority</span>
    <span class="badge" style="background:#f1f5f9;color:#475569">${esc(detail.category)}</span>
  </div>

  ${detail.description ? `<div class="section-label">Description</div><p class="desc">${nl(detail.description)}</p>` : ''}

  <div class="section-label">Details</div>
  <div class="grid">
    <div class="field"><div class="field-label">Assigned To</div><div class="field-value">${esc(detail.assignedTo || 'Unassigned')}</div></div>
    <div class="field"><div class="field-label">Requested By</div><div class="field-value">${esc(detail.requestedBy || '—')}</div></div>
    <div class="field"><div class="field-label">Scheduled Date</div><div class="field-value">${esc(fmt.date(detail.scheduledDate))}</div></div>
    <div class="field"><div class="field-label">Completed Date</div><div class="field-value">${esc(fmt.date(detail.completedDate))}</div></div>
    <div class="field"><div class="field-label">Cost</div><div class="field-value">${esc(detail.cost ? fmt.currency(detail.cost) : 'TBD')}</div></div>
    <div class="field"><div class="field-label">Category</div><div class="field-value">${esc(detail.category)}</div></div>
  </div>

  ${detail.notes ? `<div class="section-label">Notes</div><div class="notes-box">${nl(detail.notes)}</div>` : ''}

  ${detail.images?.length > 0 ? `
  <div class="section-label">Attachments (${detail.images.length})</div>
  <div class="images">${imageBlocks}</div>` : ''}

  <div class="footer">
    <span>PropManager · Property Management System</span>
    <span>Printed ${new Date().toLocaleString()}</span>
  </div>
</div>
<script>
  window.onload = function() {
    var imgs = Array.from(document.images);
    if (!imgs.length) { window.print(); return; }
    var done = 0;
    function tryPrint() { if (++done === imgs.length) window.print(); }
    imgs.forEach(function(img) {
      if (img.complete) tryPrint();
      else { img.addEventListener('load', tryPrint); img.addEventListener('error', tryPrint); }
    });
  };
<\/script>
</body>
</html>`;

    const pw = window.open('', '_blank', 'width=860,height=760,scrollbars=yes');
    if (!pw) { toast.warning('Allow pop-ups to print'); return; }
    pw.document.write(html);
    pw.document.close();
  };

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">{t('workOrders')}</h1><p className="page-subtitle">{t('maintenanceRepairTracking')}</p></div>
        <div style={{ display:'flex', gap:10 }}>
          <button className="btn btn-secondary btn-icon" onClick={load}><RefreshCw size={16}/></button>
          {canCreate && <button className="btn btn-primary" onClick={openModal}><Plus size={16}/>{t('newWorkOrder')}</button>}
        </div>
      </div>

      {/* Status tiles */}
      <div className="grid grid-4 mb-6">
        {[
          ['open',       t('open'),      'var(--rose)',    <AlertCircle  size={20}/>],
          ['in_progress',t('inProgress'),'var(--brand)',   <Clock        size={20}/>],
          ['scheduled',  t('scheduled'), 'var(--amber)',   <Clock        size={20}/>],
          ['completed',  t('completed'), 'var(--emerald)', <CheckCircle2 size={20}/>],
        ].map(([s,l,c,ic]) => (
          <div key={s} className="card"
            style={{ padding:'16px 18px', display:'flex', alignItems:'center', gap:14, cursor:'pointer', border:statusF===s?`1.5px solid ${c}`:'1px solid var(--border)' }}
            onClick={() => setStatusF(statusF === s ? '' : s)}>
            <div style={{ width:40,height:40,borderRadius:10,background:c+'18',display:'flex',alignItems:'center',justifyContent:'center',color:c }}>{ic}</div>
            <div><div style={{ fontSize:22,fontWeight:800 }}>{counts(s)}</div><div style={{ fontSize:12,color:'var(--ink-40)' }}>{l}</div></div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <div className="search-bar" style={{ flex:1, minWidth:200 }}>
          <Search size={15} color="var(--ink-20)"/>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('searchWorkOrders')}/>
        </div>
        <select className="form-input form-select" style={{ flex:'1 1 140px', minWidth:120 }} value={statusF} onChange={e => setStatusF(e.target.value)}>
          <option value="">{t('allStatus')}</option>
          {['open','scheduled','in_progress','completed'].map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </select>
        <select className="form-input form-select" style={{ flex:'1 1 130px', minWidth:110 }} value={prioF} onChange={e => setPrioF(e.target.value)}>
          <option value="">{t('allPriority')}</option>
          {['high','medium','low'].map(p => <option key={p} value={p}>{statusLabel(p)}</option>)}
        </select>
      </div>

      <div className="card">
        <div style={{ overflowX:'auto' }}>
          {loading ? <div style={{ padding:40,textAlign:'center' }}><div className="spinner" style={{ margin:'0 auto' }}/></div> : (
            <table className="data-table">
              <thead>
                <tr><th style={{ width:1,whiteSpace:'nowrap' }}></th><th>{t('workOrder')}</th><th>{t('property')}</th><th>{t('category')}</th><th>{t('priority')}</th><th>{t('status')}</th><th>{t('assigned')}</th><th>{t('scheduled')}</th><th>{t('cost')}</th></tr>
              </thead>
              <tbody>
                {wos.map(w => (
                  <tr key={w._id}>
                    <td className="table-actions"><div style={{ display:'flex', gap:6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setDetail(w)}>{t('view')}</button>
                      <button className="btn btn-secondary btn-sm btn-icon" onClick={() => handlePrint(w)} title={t('print')}><Printer size={13}/></button>
                      {isStaff && canEdit && w.status !== 'completed' && <button className="btn btn-success btn-sm" onClick={() => updateStatus(w._id,'completed')}>{t('done')}</button>}
                      {isStaff && canEdit && <button className="btn btn-secondary btn-sm btn-icon" onClick={() => openEdit(w)} title={t('edit')}><Edit2 size={13}/></button>}
                      {canDelete && <button className="btn btn-danger btn-sm btn-icon" onClick={() => setConfirmDel(w)} title={t('delete')}><Trash2 size={13}/></button>}
                    </div></td>
                    <td>
                      <div style={{ fontWeight:600,fontSize:13,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{w.title}</div>
                      <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:2 }}>
                        <span style={{ fontSize:11,color:'var(--ink-40)' }}>{fmt.date(w.createdAt)}</span>
                        {w.images?.length > 0 && <Paperclip size={11} color="var(--ink-20)" title={`${w.images.length} attachment(s)`}/>}
                      </div>
                    </td>
                    <td style={{ fontSize:13 }}>
                      <div>{w.propertyId?.name || '—'}</div>
                      {w.apartmentId && <div style={{ fontSize:11,color:'var(--ink-40)' }}>Unit #{w.apartmentId.unit}</div>}
                    </td>
                    <td><span className="badge badge-neutral">{w.category}</span></td>
                    <td><span className={`badge ${priorityClass(w.priority)}`} style={{ display:'flex',alignItems:'center',gap:4,width:'fit-content' }}>{PICONS[w.priority]}{statusLabel(w.priority)}</span></td>
                    <td><span className={`badge ${statusClass(w.status)}`}>{statusLabel(w.status)}</span></td>
                    <td style={{ fontSize:13 }}>{w.assignedTo || <span style={{ color:'var(--ink-20)' }}>Unassigned</span>}</td>
                    <td style={{ fontSize:12 }}>{fmt.date(w.scheduledDate)}</td>
                    <td style={{ fontSize:13,fontWeight:w.cost?700:400 }}>{w.cost ? fmt.currency(w.cost) : <span style={{ color:'var(--ink-20)' }}>—</span>}</td>
                  </tr>
                ))}
                {wos.length === 0 && <tr><td colSpan={9} style={{ textAlign:'center',padding:40,color:'var(--ink-40)' }}>{t('noWorkOrders')}</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create Modal */}
      <Modal open={modal} onClose={() => setModal(false)} title={t('newWorkOrder')} size="lg"
        footer={<><button className="btn btn-secondary" onClick={() => setModal(false)}>{t('cancel')}</button><button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? t('saving') : t('submit')}</button></>}>
        <div className="form-grid form-grid-2" style={{ gap:16 }}>
          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label className="form-label">{t('title')} *</label>
            <input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title:e.target.value }))} placeholder={t('briefDescription')}/>
          </div>
          {isStaff && (
            <div className="form-group">
              <label className="form-label">{t('property')} *</label>
              <select className="form-input form-select" value={form.propertyId} onChange={e => setForm(f => ({ ...f, propertyId:e.target.value, apartmentId:'' }))}>
                <option value="">{t('select')}</option>
                {props.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
              </select>
            </div>
          )}
          {isStaff && (
            <div className="form-group">
              <label className="form-label">{t('unitOptional')}</label>
              <select className="form-input form-select" value={form.apartmentId} onChange={e => setForm(f => ({ ...f, apartmentId:e.target.value }))}>
                <option value="">{t('commonArea')}</option>
                {propApts.map(a => <option key={a._id} value={a._id}>{t('unit')} #{a.unit}</option>)}
              </select>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">{t('category')}</label>
            <select className="form-input form-select" value={form.category} onChange={e => setForm(f => ({ ...f, category:e.target.value }))}>
              {CATS.map(c => <option key={c} value={c}>{t(CAT_KEYS[c])}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('priority')}</label>
            <select className="form-input form-select" value={form.priority} onChange={e => setForm(f => ({ ...f, priority:e.target.value }))}>
              {['high','medium','low'].map(pri => <option key={pri} value={pri}>{t(pri)}</option>)}
            </select>
          </div>
          {isStaff && (
            <div className="form-group">
              <label className="form-label">{t('assignedTo')}</label>
              <input className="form-input" value={form.assignedTo} onChange={e => setForm(f => ({ ...f, assignedTo:e.target.value }))} placeholder={t('contractorStaff')}/>
            </div>
          )}
          {isStaff && (
            <div className="form-group">
              <label className="form-label">{t('scheduledDate')}</label>
              <input className="form-input" type="date" value={form.scheduledDate} onChange={e => setForm(f => ({ ...f, scheduledDate:e.target.value }))}/>
            </div>
          )}
          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label className="form-label">{t('description')}</label>
            <textarea className="form-input" rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description:e.target.value }))} placeholder={t('detailedDescription')}/>
          </div>
          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label className="form-label">{t('notes')}</label>
            <textarea className="form-input" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes:e.target.value }))} placeholder={t('additionalNotes')}/>
          </div>

          {/* Photo / document attachments */}
          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label className="form-label">{t('attachments')}</label>
            <div
              style={{ border:'2px dashed var(--border)', borderRadius:8, padding:'14px 16px', cursor:'pointer', display:'flex', alignItems:'center', gap:10, background:'transparent' }}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" style={{ display:'none' }} multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.heic"
                onChange={e => setFiles(prev => [...prev, ...Array.from(e.target.files)])} />
              <Paperclip size={18} color="var(--ink-20)"/>
              <span style={{ fontSize:13, color:'var(--ink-40)' }}>{t('clickToAttachDamageProof')}</span>
            </div>
            {files.length > 0 && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginTop:10 }}>
                {files.map((f, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:6, background:'var(--surface-2)', borderRadius:6, padding:'5px 10px', fontSize:12 }}>
                    <Paperclip size={11} color="var(--ink-40)"/>
                    <span style={{ maxWidth:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name}</span>
                    <button type="button" onClick={() => removeFile(i)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--rose)', lineHeight:1 }}><X size={11}/></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Detail Modal */}
      {detail && (
        <Modal open={!!detail} onClose={() => setDetail(null)} title={t('workOrderDetail')} size="lg"
          footer={
            <div style={{ display:'flex', justifyContent:'space-between', width:'100%' }}>
              <div style={{ display:'flex', gap:8 }}>
                {isStaff && canEdit && detail.status === 'open'      && <button className="btn btn-secondary" onClick={() => updateStatus(detail._id,'in_progress')}>{t('startProgress')}</button>}
                {isStaff && canEdit && detail.status !== 'completed' && <button className="btn btn-success"   onClick={() => updateStatus(detail._id,'completed')}>{t('markComplete')}</button>}
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button className="btn btn-secondary" onClick={() => handlePrint(detail, imageUrls)} style={{ gap:7 }}>
                  <Printer size={14}/>{t('print')}
                </button>
                <button className="btn btn-secondary" onClick={() => setDetail(null)}>{t('close')}</button>
              </div>
            </div>
          }>
          <div style={{ display:'flex', flexDirection:'column', gap:18 }}>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <span className={`badge ${statusClass(detail.status)}`}>{statusLabel(detail.status)}</span>
              <span className={`badge ${priorityClass(detail.priority)}`}>{statusLabel(detail.priority)} priority</span>
              <span className="badge badge-neutral">{detail.category}</span>
            </div>
            <div>
              <h3 style={{ fontSize:17,fontWeight:700,marginBottom:8 }}>{detail.title}</h3>
              <p style={{ fontSize:14,color:'var(--ink-60)',lineHeight:1.7 }}>{detail.description || t('noDescription')}</p>
            </div>
            <div className="form-grid form-grid-2" style={{ gap:10 }}>
              {[
                [t('property'),    detail.propertyId?.name],
                [t('unit'),        detail.apartmentId ? '#' + detail.apartmentId.unit : 'Common Area'],
                [t('assignedTo'),  detail.assignedTo || 'Unassigned'],
                [t('requestedBy'), detail.requestedBy || '—'],
                [t('created'),     fmt.date(detail.createdAt)],
                [t('scheduled'),   fmt.date(detail.scheduledDate)],
                [t('completed'),   fmt.date(detail.completedDate)],
                [t('cost'),        detail.cost ? fmt.currency(detail.cost) : 'TBD'],
              ].map(([k, v]) => (
                <div key={k} style={{ background:'var(--surface-2)', borderRadius:8, padding:'10px 13px' }}>
                  <div style={{ fontSize:10,color:'var(--ink-40)',fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:2 }}>{k}</div>
                  <div style={{ fontSize:14,fontWeight:600 }}>{v || '—'}</div>
                </div>
              ))}
            </div>
            {detail.notes && (
              <div style={{ background:'var(--surface-2)', borderRadius:8, padding:14 }}>
                <div style={{ fontSize:10,fontWeight:700,color:'var(--ink-40)',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:6 }}>{t('notes').toUpperCase()}</div>
                <p style={{ fontSize:14,color:'var(--ink-60)' }}>{detail.notes}</p>
              </div>
            )}

            {/* Attachments */}
            {detail.images?.length > 0 && (
              <div>
                <div style={{ fontSize:10,fontWeight:700,color:'var(--ink-40)',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:10 }}>{t('attachments')} ({detail.images.length})</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
                  {detail.images.map((key, i) => {
                    // Use the pre-fetched signed URL; fall back to the redirect URL while loading
                    const loaded = key in imageUrls;
                    const url    = imageUrls[key] || fileViewUrl(key);
                    return isImage(key) ? (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                        style={{ display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid var(--border)', borderRadius:8, overflow:'hidden', width:100, height:80, flexShrink:0, textDecoration:'none' }}>
                        {loaded && imageUrls[key] ? (
                          <img src={imageUrls[key]} alt={`attachment-${i+1}`}
                            style={{ width:100, height:80, objectFit:'cover', display:'block' }}/>
                        ) : (
                          <div style={{ width:100, height:80, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:4, background:'var(--surface-2)', color:'var(--brand)' }}>
                            <Image size={22}/>
                            <span style={{ fontSize:10, color:'var(--ink-40)' }}>{loaded ? t('view') : '…'}</span>
                          </div>
                        )}
                      </a>
                    ) : (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                        style={{ display:'flex', alignItems:'center', gap:6, background:'var(--surface-2)', borderRadius:8, padding:'8px 12px', fontSize:13, color:'var(--brand)', border:'1px solid var(--border)', textDecoration:'none' }}>
                        <Paperclip size={14}/>{t('attachment')} {i+1}
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Edit Work Order Modal */}
      <Modal open={!!editWo} onClose={() => setEditWo(null)} title={t('edit') + ' ' + t('workOrder')} size="lg"
        footer={<><button className="btn btn-secondary" onClick={() => setEditWo(null)}>{t('cancel')}</button><button className="btn btn-primary" onClick={handleEditSave} disabled={saving}>{saving ? t('saving') : t('save')}</button></>}>
        <div className="form-grid form-grid-2" style={{ gap:16 }}>
          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label className="form-label">{t('title')} *</label>
            <input className="form-input" value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title:e.target.value }))}/>
          </div>
          {isStaff && (
            <div className="form-group">
              <label className="form-label">{t('property')}</label>
              <select className="form-input form-select" value={editForm.propertyId} onChange={e => setEditForm(f => ({ ...f, propertyId:e.target.value, apartmentId:'' }))}>
                <option value="">{t('select')}</option>
                {props.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
              </select>
            </div>
          )}
          {isStaff && (
            <div className="form-group">
              <label className="form-label">{t('unitOptional')}</label>
              <select className="form-input form-select" value={editForm.apartmentId} onChange={e => setEditForm(f => ({ ...f, apartmentId:e.target.value }))}>
                <option value="">{t('commonArea')}</option>
                {editPropApts.map(a => <option key={a._id} value={a._id}>{t('unit')} #{a.unit}</option>)}
              </select>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">{t('category')}</label>
            <select className="form-input form-select" value={editForm.category} onChange={e => setEditForm(f => ({ ...f, category:e.target.value }))}>
              {CATS.map(c => <option key={c} value={c}>{t(CAT_KEYS[c])}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('priority')}</label>
            <select className="form-input form-select" value={editForm.priority} onChange={e => setEditForm(f => ({ ...f, priority:e.target.value }))}>
              {['high','medium','low'].map(p => <option key={p} value={p}>{t(p)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('status')}</label>
            <select className="form-input form-select" value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status:e.target.value }))}>
              {['open','scheduled','in_progress','completed','cancelled'].map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('assignedTo')}</label>
            <input className="form-input" value={editForm.assignedTo} onChange={e => setEditForm(f => ({ ...f, assignedTo:e.target.value }))}/>
          </div>
          <div className="form-group">
            <label className="form-label">{t('scheduledDate')}</label>
            <input className="form-input" type="date" value={editForm.scheduledDate} onChange={e => setEditForm(f => ({ ...f, scheduledDate:e.target.value }))}/>
          </div>
          <div className="form-group">
            <label className="form-label">{t('cost')}</label>
            <input className="form-input" type="number" value={editForm.cost} onChange={e => setEditForm(f => ({ ...f, cost:e.target.value }))} placeholder="0"/>
          </div>
          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label className="form-label">{t('description')}</label>
            <textarea className="form-input" rows={3} value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description:e.target.value }))}/>
          </div>
          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label className="form-label">{t('notes')}</label>
            <textarea className="form-input" rows={2} value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes:e.target.value }))}/>
          </div>
        </div>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal open={!!confirmDel} onClose={() => setConfirmDel(null)} title={t('delete') + ' ' + t('workOrder')} size="sm"
        footer={<><button className="btn btn-secondary" onClick={() => setConfirmDel(null)}>{t('cancel')}</button><button className="btn btn-danger" onClick={handleDelete}>{t('delete')}</button></>}>
        {confirmDel && <p style={{ fontSize:14, color:'var(--ink-60)' }}>Delete work order <strong>{confirmDel.title}</strong>? This cannot be undone.</p>}
      </Modal>
    </div>
  );
}
